import React, { ReactElement, useState } from "react"
import { useHistory } from "react-router-dom"
import { Paths } from "utils/Paths"
import { PageLayout } from "features/global-ui/page-layout"
import { PageHeader, PageHeaderGlyph } from "features/global-ui/page-header/page-header"
import { TalkToCarrotLegal } from "./TalkToCarrotLegal"
import { Title } from "services/page-title/Title"
import { FormattedMessage, useIntl } from "react-intl"
import { useBreakpointComponent } from "@carrotfertility/carotene"
import { HelpNode, NeedHelpLink, SmallHeaderOverride } from "./util/talkToCarrotMolecules"
import TwoColumnRightPrimary from "components/views/atoms/layouts/TwoColumnRightPrimary"
import WorldVectorMap from "./TalkToCarrotMap"
import { PageOption } from "components/dynamic-content/util/dynamicContent"
import { GuidanceElement } from "lib/contentful/components/GuidanceElement"
import { useIntlSort } from "utils/hooks/useIntlSort"
import styled from "styled-components"
import { TalkToCarrotSelectOption } from "./TalkToCarrotSelectOption"
import { Autocomplete, Button, TextField } from "@carrotfertility/carotene-core"
import { Spacer } from "components/views/atoms/Atoms"
import { useGetTalkToCarrotLocationGuidance } from "./hooks/useGetTalkToCarrotLocationGuidance"
import { FullScreenLoading } from "components/read/shared/FullScreenLoading"

interface LocalizedPageOption extends PageOption {
  formattedLabel: string
}

const LocationFooter = styled.div.attrs({ className: "flex items-center w-100 justify-center" })`
  overflow: auto;
`

const TalkToCarrotLocationPicker = ({
  talkToCarrotLocations,
  setTalkToCarrotLocations,
  hoveredLocation,
  setHoveredLocation
}: {
  talkToCarrotLocations: LocalizedPageOption[] | null
  setTalkToCarrotLocations: React.Dispatch<any>
  hoveredLocation: PageOption | null
  setHoveredLocation: React.Dispatch<React.SetStateAction<PageOption>>
}): JSX.Element => {
  const history = useHistory()
  const [selectedLocation, setSelectedLocation] = useState<PageOption>(null)
  const intl = useIntl()
  const { locale } = intl
  const { localeComparator } = useIntlSort()

  const { data: guidanceData, isLoading } = useGetTalkToCarrotLocationGuidance(locale)
  const guidance = <GuidanceElement guidance={guidanceData} />

  const onSubmit = (): void => {
    history.push(`${Paths.TALK_TO_CARROT}/${selectedLocation.slug}`)
  }

  const selectedOptionToTop = (locationOption: LocalizedPageOption): void => {
    setTalkToCarrotLocations(
      locationOption
        ? [
            locationOption,
            ...talkToCarrotLocations
              .filter(({ label }) => label !== locationOption.label)
              .sort(({ formattedLabel: labelA }, { formattedLabel: labelB }) => localeComparator(labelA, labelB))
          ]
        : talkToCarrotLocations
    )
  }

  // @ts-expect-error TS7006
  function handleSelectLocation(_event, locationOption): void {
    setHoveredLocation(null)
    setSelectedLocation(locationOption)
    selectedOptionToTop(locationOption)
  }

  const continueButton = (
    <div className={"ml-auto"}>
      <Button color="primary" size="medium" onClick={onSubmit} disabled={!selectedLocation}>
        <FormattedMessage defaultMessage="Continue" />
      </Button>
    </div>
  )

  const continueWithHelp = (
    <>
      {<NeedHelpLink baseUrl={Paths.TALK_TO_CARROT} />}
      {continueButton}
    </>
  )

  const locationSelector = (
    <Autocomplete
      aria-label={intl.formatMessage({ defaultMessage: "Preferred location" })}
      disablePortal
      multiple={false}
      onChange={handleSelectLocation}
      value={selectedLocation}
      getOptionLabel={(option) => option.formattedLabel}
      options={talkToCarrotLocations || []}
      clearOnEscape
      renderOption={(props, state) => (
        <TalkToCarrotSelectOption
          {...props}
          key={`${state.value}-${state.pageId}`}
          isSelected={state.value === selectedLocation?.value}
          onMouseOver={(...args) => {
            state.onMouseOver(...args)
          }}
          onMouseOut={state.onMouseOut}
        >
          {state.formattedLabel}
        </TalkToCarrotSelectOption>
      )}
      renderInput={(params) => (
        <TextField
          placeholder={intl.formatMessage({
            defaultMessage: "Select option"
          })}
          name={intl.formatMessage({
            defaultMessage: "location"
          })}
          helperText=" "
          {...params}
        />
      )}
    />
  )

  const PickerFooter = ({ withHelp = false }: { withHelp?: boolean }): JSX.Element => (
    <LocationFooter>{withHelp ? continueWithHelp : continueButton}</LocationFooter>
  )

  const map = (
    <WorldVectorMap
      selectedLocationId={selectedLocation ? selectedLocation.value.toLowerCase() : ""}
      hoveredLocationId={hoveredLocation ? hoveredLocation.value.toLowerCase() : ""}
      onMouseEnter={({ target }: any): void =>
        setHoveredLocation(talkToCarrotLocations.find(({ value }) => value.toLowerCase() === target.id.toLowerCase()))
      }
      onMouseLeave={(): void => setHoveredLocation(null)}
      onClick={(event: any): void => {
        const optionClicked = talkToCarrotLocations.find(
          ({ value }) => value.toLowerCase() === event?.target.id.toLowerCase()
        )
        if (optionClicked) {
          handleSelectLocation(event, optionClicked)
        }
      }}
    />
  )

  const locationPickerWithMap = (
    <div className="w-100">
      {map}
      {locationSelector}
      <PickerFooter />
    </div>
  )
  const locationPickerWithHelp = (
    <div className="w-100">
      {locationSelector}
      <PickerFooter withHelp />
    </div>
  )

  const GuidanceWithHelp = ({ guidance }: { guidance: ReactElement<typeof GuidanceElement> }): JSX.Element => (
    <>
      {guidance}
      {<HelpNode baseUrl={Paths.TALK_TO_CARROT} />}
    </>
  )

  const largeContent = (
    <TwoColumnRightPrimary
      secondaryContent={<GuidanceWithHelp guidance={guidance} />}
      primaryContent={locationPickerWithMap}
    />
  )

  const mediumContent = (
    <>
      {guidance}
      <Spacer height={3} />
      {map}
      {locationPickerWithHelp}
    </>
  )

  const smallContent = (
    <>
      <SmallHeaderOverride>
        {guidance}
        <Spacer height={2} />
        {locationPickerWithHelp}
      </SmallHeaderOverride>
    </>
  )

  const responsiveContent = useBreakpointComponent([smallContent, smallContent, mediumContent, largeContent])

  const masterLayoutTitle = intl.formatMessage({
    defaultMessage: "Talk to Carrot"
  })

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <>
      {talkToCarrotLocations && (
        <PageLayout
          footer={<TalkToCarrotLegal />}
          header={
            <PageHeader
              startGlyph={
                <PageHeaderGlyph src="/images/icn-talk-to-carrot.png" srcSet={"/images/icn-talk-to-carrot-2x.png"} />
              }
              pageTitle={<FormattedMessage defaultMessage="Talk to Carrot" />}
            />
          }
        >
          <Title
            title={intl.formatMessage({
              defaultMessage: "Preferred location"
            })}
            productTitle={masterLayoutTitle}
          />
          {guidance && responsiveContent}
        </PageLayout>
      )}
    </>
  )
}

export { TalkToCarrotLocationPicker }
